import {orderStatusMapping, SFCC_ORDER_STATUS} from '../constant'
import {PDFDocument, StandardFonts, rgb} from 'pdf-lib'
import download from 'downloadjs'
import fontkit from '@pdf-lib/fontkit'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

export const getInfoLabelVariantByOmsStatus = (omsStatus) => {
    if (typeof omsStatus !== 'string') return ''

    const statusMapping = {
        'partially delivered': 'light',
        'partially shipped': 'light',
        'return requested': 'light',
        active: 'coupon',
        'vat check': '',
        delivered: 'coupon',
        shipped: 'coupon',
        processing: '',
        canceled: 'grey',
        'export Error': 'error',
        'non-conform': 'error',
        declined: 'error',
        expired: 'error',
        rejected: 'error',
        completed: 'coupon',
        failed: 'grey',
        dismissed: 'grey',
    }

    return statusMapping[omsStatus]
}

/**
 * Determines whether the "Edit" or "Cancel" actions should be hidden on subscriptions based on its status.
 *
 * @param {string} status - The current status of the order.
 * @returns {boolean} Returns true if the status is 'canceled', 'expired', or 'dismissed'; otherwise, false.
 */
export const isEditOrCancelHidden = (status) => {
    const hiddenStatuses = ['canceled', 'expired', 'dismissed']
    return hiddenStatuses.includes((status || '').toLowerCase())
}

// const getOrderFlow = (order, isReturn = false) => {
//     //add flow checks
//     // if (order?.OrderPaymentSummaries?.records?.[0]?.Refunds) return 'return'
//     // if (order?.referenceId === 'refSubscription' && order?.Status__c === 'Canceled')
//     //     return 'subscriptionCancelation'
//     // if (order?.fulfillmentOrder) return 'fulfillment'

//     // if (order?.returnOrder?.length > 0) return 'return'
//     if (!isReturn) return 'simple'
//     if (isReturn) return 'return'
// }

const getDefaultMessage = () => ({
    defaultMessage: 'Not found',
    id: 'not_found',
})

const getDefaultStatus = () => ({
    message: getDefaultMessage(),
    status: 'Not found',
})

const mapOmsToSfccStatus = (omsStatus) => {
    for (const flow in orderStatusMapping) {
        const mapping = orderStatusMapping[flow]?.find((m) =>
            m?.oms?.map((s) => s?.toLowerCase()?.trim()).includes(omsStatus?.toLowerCase()?.trim()),
        )
        if (mapping) {
            return mapping?.sfcc
        }
    }
    return null
}

/**
 * Determines the order status based on the table logic when there are multiple shipments.
 *
 * @param {string} orderStatus - The overall order status.
 * @param {Array<string>} shipmentStatuses - The statuses of individual shipments.
 * @returns {string} The calculated order status.
 */

const calculateOrderStatusForMultipleShipments = (orderStatus, shipmentStatuses) => {
    const normalizedStatuses = shipmentStatuses?.map((s) => mapOmsToSfccStatus(s))?.filter(Boolean)
    if (
        normalizedStatuses.length > 0 &&
        normalizedStatuses.every((status) => status === SFCC_ORDER_STATUS.DELIVERED)
    ) {
        return SFCC_ORDER_STATUS.DELIVERED
    }

    if (
        normalizedStatuses.length > 0 &&
        normalizedStatuses.every((status) => status === SFCC_ORDER_STATUS.SHIPPED)
    ) {
        return SFCC_ORDER_STATUS.SHIPPED
    }

    if (
        normalizedStatuses.length > 0 &&
        normalizedStatuses.every((status) => status === SFCC_ORDER_STATUS.PROCESSING)
    ) {
        return SFCC_ORDER_STATUS.PROCESSING
    }

    if (
        normalizedStatuses.includes(SFCC_ORDER_STATUS.DELIVERED) &&
        normalizedStatuses.some((status) => status !== SFCC_ORDER_STATUS.DELIVERED)
    ) {
        return SFCC_ORDER_STATUS.PARTIALLY_DELIVERED
    }

    if (
        normalizedStatuses.includes(SFCC_ORDER_STATUS.SHIPPED) &&
        normalizedStatuses.some((status) => status !== SFCC_ORDER_STATUS.SHIPPED)
    ) {
        return SFCC_ORDER_STATUS.PARTIALLY_SHIPPED
    }

    return mapOmsToSfccStatus(orderStatus?.toLowerCase())
}

/**
 *
 * @param {status} status
 * @param {isReturnFlow} isReturn
 * @returns {Object} An object containing a message and a status:
 *
 * Example:
 *
 * - message: {id: 'status.cancellation', defaultMessage: 'Cancellation'}
 * - status: 'Cancellation'
 *
 */
export const getOrderStatus = (status, isReturnFlow = false, shipmentStatuses = []) => {
    if (!status || typeof status !== 'string') return getDefaultStatus()

    const normalizedStatus = status?.toLowerCase()?.trim()

    // Handle multiple shipments logic
    if (shipmentStatuses.length > 0) {
        const calculatedStatus = calculateOrderStatusForMultipleShipments(
            normalizedStatus,
            shipmentStatuses,
        )
        return {
            message: {id: `status.${calculatedStatus}`, defaultMessage: calculatedStatus},
            status: calculatedStatus,
        }
    }

    const orderFlow = isReturnFlow ? 'return' : 'simple'

    if (!orderStatusMapping[orderFlow]) return getDefaultStatus()

    const statusMappings = orderStatusMapping[orderFlow] || []
    const mapping = statusMappings.find((m) =>
        m.oms.map((s) => s.toLowerCase().trim()).includes(normalizedStatus),
    )
    return {
        message: mapping?.message ?? getDefaultMessage(),
        status: mapping?.sfcc ?? 'Not found',
    }
}

export const isReturnEligible = (deliveryDate) => {
    if (!deliveryDate) return

    const deliveredDate = new Date(deliveryDate)
    const today = new Date()
    const cutoffDate = new Date(deliveredDate)

    cutoffDate.setDate(deliveredDate.getDate() + 30)

    return today <= cutoffDate
}

/**
 * Determines if an order has been exported based on its status.
 *
 * @param {string} orderStatus - The status of the order.
 * @returns {boolean} Returns `true` if the order is exported, otherwise `false`.
 */
export const isOrderExported = (orderStatus) => {
    if (
        orderStatus &&
        (orderStatus?.toLowerCase() === 'created' ||
            orderStatus?.toLowerCase() === 'grace period' ||
            orderStatus?.toLowerCase() === 'export error')
    ) {
        return false
    }
    return true
}

/**
 * Calculates the item's price based on its status.
 *
 * @param {string} status - The status to check.
 * @param {object} item - The item object containing price details.
 * @returns {number} Returns the calculated price based on the status.
 */
export const getItemPriceByStatus = (status, item) => {
    if (status?.toLowerCase() === 'canceled') {
        return item?.OriginalOrderItem?.TotalAmtWithTax / item?.QuantityOrdered
    }
    return item?.TotalLineAmountWithTax / item?.Quantity
}

export const splitTextIntoLines = (text, columnWidth, fontSize) => {
    const maxCharsPerLine = Math.floor(columnWidth / (fontSize * 0.6))
    const words = text.split(' ')
    const lines = []
    let currentLine = ''

    words.forEach((word) => {
        if ((currentLine + word).length <= maxCharsPerLine) {
            currentLine += `${word} `
        } else {
            lines.push(currentLine.trim())
            currentLine = `${word} `
        }
    })

    if (currentLine.trim()) {
        lines.push(currentLine.trim())
    }

    return lines
}

/**
 * Generates a PDF document for return order details and triggers a download.
 * @returns {Promise<void>} A promise that resolves when the PDF is generated and downloaded.
 */
export const generateReturnPDF = async (returnOrder, formatMessage, order) => {
    const pdfDoc = await PDFDocument.create()
    pdfDoc.registerFontkit(fontkit)
    const page = pdfDoc.addPage([595.28, 841.89]) // A4 size in points
    const fontRegularUrl = getAssetUrl('static/fonts/global/NotoSans/NotoSans-Regular.ttf')
    const fontBoldUrl = getAssetUrl('static/fonts/global/NotoSans/NotoSans-Bold.ttf')

    const {width, height} = page.getSize()
    let currentY = height - 50

    // Embed NotoSans fonts
    let fontBold, fontRegular
    try {
        const [boldBytes, regularBytes] = await Promise.all([
            fetch(fontBoldUrl).then((res) => {
                if (!res.ok) throw new Error('Bold font not found')
                return res.arrayBuffer()
            }),
            fetch(fontRegularUrl).then((res) => {
                if (!res.ok) throw new Error('Regular font not found')
                return res.arrayBuffer()
            }),
        ])
        // Check if the font files are valid TTF files
        if (boldBytes.byteLength === 0 || regularBytes.byteLength === 0) {
            throw new Error('Font file is empty')
        }
        fontBold = await pdfDoc.embedFont(boldBytes)
        fontRegular = await pdfDoc.embedFont(regularBytes)
    } catch (e) {
        console.error('Font load/embed error:', e)
        // fallback to standard fonts if custom fonts fail
        fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
        fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica)
    }

    // Title
    const title = formatMessage({
        id: 'return_form_information.title',
        defaultMessage: 'Return Information',
    })
    page.drawText(title, {
        x: (width - fontBold.widthOfTextAtSize(title, 24)) / 2, // Center the title
        y: currentY,
        size: 24,
        color: rgb(0, 0, 0),
        font: fontBold,
    })
    currentY -= 30

    // Subtitle
    const subtitle = formatMessage({
        id: 'return_form_information.subtitle',
        defaultMessage: 'Returns can be submitted until:',
    })
    page.drawText(subtitle, {
        x: (width - fontRegular.widthOfTextAtSize(subtitle, 14)) / 2, // Center the subtitle
        y: currentY,
        size: 14,
        color: rgb(0, 0, 0),
        font: fontRegular,
    })

    // Calculate and display max return date
    let maxReturnDateStr = ''
    if (order?.orderedDate && order?.countrySettings?.[0]?.MaximumDaysForReturn__c) {
        const createdDate = new Date(order.orderedDate)
        const maxDays = Number(order.countrySettings[0].MaximumDaysForReturn__c)
        const maxReturnDate = new Date(createdDate)
        maxReturnDate.setDate(createdDate.getDate() + maxDays)
        // Format as yyyy-mm-dd
        maxReturnDateStr = maxReturnDate.toLocaleDateString('it-IT', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        })
        page.drawText(maxReturnDateStr, {
            x: (width - fontRegular.widthOfTextAtSize(maxReturnDateStr, 14)) / 2,
            y: currentY - 18,
            size: 14,
            color: rgb(0, 0, 0),
            font: fontRegular,
        })
        currentY -= 18
    }
    currentY -= 40

    // Column headers
    const columnWidth = width / 3 - 20
    const leftX = 80
    const centerX = leftX + columnWidth
    const rightX = centerX + columnWidth - 30

    // Draw multiline headers instead of single-line
    const headerFontSize = 10
    const headerMaxWidth = columnWidth - 20

    const leftHeader = formatMessage({
        id: 'return_form_order_details',
        defaultMessage: 'Order Details',
    })
    const centerHeader = formatMessage({
        id: 'return_form_details',
        defaultMessage: 'Return Details',
    })
    const rightHeader = formatMessage({
        id: 'return_form_shipping_address',
        defaultMessage: 'Shipping Address',
    })

    /**
     * Draws a multiline column header, splitting the text by spaces if it exceeds maxWidth.
     * @param {string} text - The header text.
     * @param {number} x - The x position.
     * @param {number} y - The starting y position.
     * @param {PDFont} font - The font to use.
     * @param {number} fontSize - The font size.
     * @param {number} maxWidth - The maximum width allowed for a line.
     * @returns {number} The total height used by the header (for y adjustment).
     */
    function drawMultilineHeader(text, x, y, font, fontSize, maxWidth) {
        // Split the text into words and build lines that fit within maxWidth
        const words = text.split(' ')
        let lines = []
        let currentLine = ''
        words.forEach((word) => {
            const testLine = currentLine ? currentLine + ' ' + word : word
            const testWidth = font.widthOfTextAtSize(testLine, fontSize)
            if (testWidth <= maxWidth) {
                currentLine = testLine
            } else {
                if (currentLine) lines.push(currentLine)
                currentLine = word
            }
        })
        if (currentLine) lines.push(currentLine)

        // Draw each line
        lines.forEach((line, idx) => {
            page.drawText(line, {
                x,
                y: y - idx * (fontSize + 2),
                size: fontSize,
                color: rgb(0, 0, 0),
                font,
            })
        })
        return lines.length * (fontSize + 2)
    }

    // Draw and get the max height used
    const leftHeaderHeight = drawMultilineHeader(
        leftHeader,
        leftX,
        currentY,
        fontBold,
        headerFontSize,
        headerMaxWidth,
    )
    const centerHeaderHeight = drawMultilineHeader(
        centerHeader,
        centerX,
        currentY,
        fontBold,
        headerFontSize,
        headerMaxWidth,
    )
    const rightHeaderHeight = drawMultilineHeader(
        rightHeader,
        rightX,
        currentY,
        fontBold,
        headerFontSize,
        headerMaxWidth,
    )

    // Adjust currentY by the tallest header
    currentY -= Math.max(leftHeaderHeight, centerHeaderHeight, rightHeaderHeight) + 5

    // Row Y positions
    let rowY = currentY

    /**
     * Draws a label and its corresponding value on a PDF page, adjusting position based on label width.
     * Uses a separate threshold for label wrapping and for value positioning.
     *
     * @param {string} label - The text label to draw.
     * @param {string} value - The value associated with the label to draw.
     * @param {number} x - The x-coordinate for drawing the label.
     * @param {number} y - The y-coordinate for drawing the label.
     * @param {PDFont} fontLabel - The font to use for the label.
     * @param {PDFont} fontValue - The font to use for the value.
     * @param {number} [valueThreshold=40] - The maximum width of the label before the value is drawn below (for value positioning).
     * @param {number} [spacing=30] - The horizontal spacing between the label and value.
     * @param {number|null} [overrideSpacing=null] - Optional custom spacing between label and value.
     * @param {number} [labelThreshold=100] - The maximum width for label line wrapping (for label only).
     * @returns {number} The extra vertical spacing needed if the value is drawn below the label, otherwise 0.
     */
    function drawLabelValue(
        label,
        value,
        x,
        y,
        fontLabel,
        fontValue,
        valueThreshold = 40,
        spacing = 30,
        overrideSpacing = null, // param for custom spacing
        labelThreshold = 100, // param for label wrapping
    ) {
        // Split label into lines if needed (using labelThreshold)
        const words = label.split(' ')
        let lines = []
        let currentLine = ''
        words.forEach((word) => {
            const testLine = currentLine ? currentLine + ' ' + word : word
            const testWidth = fontLabel.widthOfTextAtSize(testLine, 8)
            if (testWidth <= labelThreshold) {
                currentLine = testLine
            } else {
                if (currentLine) lines.push(currentLine)
                currentLine = word
            }
        })
        if (currentLine) lines.push(currentLine)

        // If label is wrapped, always put value below
        if (lines.length > 1) {
            lines.forEach((line, idx) => {
                page.drawText(line, {
                    x,
                    y: y - idx * 10,
                    size: 8,
                    font: fontLabel,
                })
            })
            page.drawText(value, {
                x,
                y: y - lines.length * 10,
                size: 8,
                font: fontValue,
            })
            return lines.length * 10
        }

        // If label is not wrapped, use valueThreshold for value positioning
        const labelWidth = fontLabel.widthOfTextAtSize(label, 8)
        if (labelWidth > valueThreshold) {
            page.drawText(label, {
                x,
                y,
                size: 8,
                font: fontLabel,
            })
            page.drawText(value, {
                x,
                y: y - 10,
                size: 8,
                font: fontValue,
            })
            return 10 // extra spacing needed
        } else {
            page.drawText(label, {
                x,
                y,
                size: 8,
                font: fontLabel,
            })
            page.drawText(value, {
                x: x + (overrideSpacing !== null ? overrideSpacing : spacing),
                y,
                size: 8,
                font: fontValue,
            })
            return 0
        }
    }

    // Row 1: Placed / Returned / Name
    let placedExtra = drawLabelValue(
        formatMessage({id: 'return_form_placed', defaultMessage: 'Placed:'}),
        returnOrder.orderDate,
        leftX,
        rowY,
        fontBold,
        fontRegular,
        10, // low threshold for placed date so that the value is always below the label
        30,
        45, // more x space for "placed" value if on same line
        100,
    )
    let returnedExtra = drawLabelValue(
        formatMessage({id: 'return_form_returned', defaultMessage: 'Returned:'}),
        returnOrder.returnDate,
        centerX,
        rowY,
        fontBold,
        fontRegular,
        10, // low threshold for returned date so that the value is always below the label
        30,
        null,
        100,
    )
    let nameExtra = drawLabelValue(
        formatMessage({id: 'return_form_name', defaultMessage: 'Name:'}),
        returnOrder.customerName,
        rightX,
        rowY,
        fontBold,
        fontRegular,
        10, // low threshold for name so that the value is always below the label
        30,
        null,
        100,
    )

    rowY -= 15 + Math.max(placedExtra, returnedExtra, nameExtra)

    // Row 2: Order Number / Return Number / Email
    let orderNumberExtra = drawLabelValue(
        formatMessage({id: 'return_form_order_number', defaultMessage: 'Order Number:'}),
        returnOrder.orderNumber,
        leftX,
        rowY,
        fontBold,
        fontRegular,
        10, // low threshold for order number so that the value is always below the label
        65,
        null,
        100,
    )
    let returnNumberExtra = drawLabelValue(
        formatMessage({id: 'return_form_return_number', defaultMessage: 'Return Number:'}),
        returnOrder.returnNumber,
        centerX,
        rowY,
        fontBold,
        fontRegular,
        10, // low threshold for return number so that the value is always below the label
        65,
        null,
        100,
    )
    let emailExtra = drawLabelValue(
        formatMessage({id: 'return_form_email', defaultMessage: 'E-mail:'}),
        returnOrder.customerEmail,
        rightX,
        rowY,
        fontBold,
        fontRegular,
        10, // low threshold for email so that the value is always below the label
        30,
        null,
        100,
    )

    rowY -= 15 + Math.max(orderNumberExtra, returnNumberExtra, emailExtra)

    // Row 3: Tracking Number / Address
    let trackingLabel = formatMessage({
        id: 'return_form_tracking_number',
        defaultMessage: 'Tracking Number:',
    })
    let trackingValue = returnOrder.trackingNumber
    let trackingExtra = drawLabelValue(
        trackingLabel,
        trackingValue,
        centerX,
        rowY,
        fontBold,
        fontRegular,
        10, // low threshold for tracking number so that the value is always below the label
        30,
        null,
        100,
    )

    let addressLabel = formatMessage({id: 'return_form_address', defaultMessage: 'Address:'})
    let addressValue = returnOrder.customerAddress
    let addressExtra = drawLabelValue(
        addressLabel,
        addressValue,
        rightX,
        rowY,
        fontBold,
        fontRegular,
        10, // low threshold for address so that the value is always below the label
        30,
        45,
        100,
    )

    // Adjust rowY for the next section
    rowY -= Math.max(trackingExtra, addressExtra)
    rowY -= 30

    // Divider
    page.drawLine({
        start: {x: 50, y: rowY},
        end: {x: width - 50, y: rowY},
        thickness: 1,
        color: rgb(0, 0, 0),
    })
    rowY -= 50

    // Product details
    returnOrder.products.forEach((product, index) => {
        page.drawText(
            formatMessage({id: 'return_form_product_name', defaultMessage: 'Product Name:'}),
            {
                x: 50,
                y: rowY,
                size: 8,
                font: fontBold,
            },
        )
        page.drawText(product.name, {x: 150, y: rowY, size: 8, font: fontRegular})
        rowY -= 12

        page.drawText(
            formatMessage({id: 'return_form_item_number', defaultMessage: 'Item Number:'}),
            {
                x: 50,
                y: rowY,
                size: 8,
                font: fontBold,
            },
        )
        page.drawText(product.sku, {x: 150, y: rowY, size: 8, font: fontRegular})
        rowY -= 12

        page.drawText(formatMessage({id: 'return_form_quantity', defaultMessage: 'Quantity:'}), {
            x: 50,
            y: rowY,
            size: 8,
            font: fontBold,
        })
        page.drawText(product.quantity.toString(), {
            x: 150,
            y: rowY,
            size: 8,
            font: fontRegular,
        })
        rowY -= 12

        page.drawText(formatMessage({id: 'return_form_price', defaultMessage: 'Price:'}), {
            x: 50,
            y: rowY,
            size: 8,
            font: fontBold,
        })

        // Convert currency code to symbol
        const currencySymbol = new Intl.NumberFormat('en', {
            style: 'currency',
            currency: product.currency,
        })
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value

        page.drawText(`${product.price} ${currencySymbol || ''}`, {
            x: 150,
            y: rowY,
            size: 8,
            font: fontRegular,
        })
        rowY -= 20

        // Divider between products
        if (index < returnOrder.products.length - 1) {
            page.drawLine({
                start: {x: 50, y: rowY},
                end: {x: width - 50, y: rowY},
                thickness: 1,
                color: rgb(0, 0, 0),
            })
            rowY -= 15
        }
    })

    rowY -= 30
    // Barcode
    if (returnOrder.barcodeImage) {
        try {
            const barcodeImage = await pdfDoc.embedPng(returnOrder.barcodeImage)
            const barcodeDims = barcodeImage.scale(0.5)
            // Divider
            page.drawLine({
                start: {x: 50, y: rowY},
                end: {x: width - 50, y: rowY},
                thickness: 1,
                color: rgb(0, 0, 0),
            })
            page.drawImage(barcodeImage, {
                x: (width - barcodeDims.width) / 2,
                y: rowY - barcodeDims.height - 15,
                width: barcodeDims.width,
                height: barcodeDims.height,
            })

            // Draw return number under the barcode image
            const barcodeString = returnOrder.returnNumber
            page.drawText(barcodeString, {
                x: (width - fontRegular.widthOfTextAtSize(barcodeString, 12)) / 2,
                y: rowY - barcodeDims.height - 30,
                size: 12,
                font: fontRegular,
                color: rgb(0, 0, 0),
            })
            rowY -= barcodeDims.height + 35
        } catch (e) {
            // Barcode is not a valid PNG, skip rendering barcode
            console.error('Error embedding barcode image:', e)
        }
    }

    // Save and download the PDF
    const pdfBytes = await pdfDoc.save()
    download(pdfBytes, `return_${returnOrder.returnNumber}.pdf`, 'application/pdf')
}

/**
 * Handles the generation and printing of a return form PDF for a given return order and its associated order.
 *
 * @async
 * @function handlePrintReturnForm
 * @param {Object} returnOrder - The return order object containing details about the return.
 * @param {Object} order - The original order object associated with the return.
 * @param {Function} formatMessage - A function to format localized messages.
 * @param {Function} formatDate - A function to format dates.
 * @returns {Promise<void>} - A promise that resolves when the PDF generation is complete.
 */
export const handlePrintReturnForm = async (returnOrder, order, formatMessage, formatDate) => {
    const products = returnOrder?.ReturnOrderLineItems?.records
        .filter(
            (lineItem) =>
                lineItem.OrderItemSummary?.Name?.toLowerCase() !== 'shipping' &&
                lineItem.OrderItemSummary?.Name?.toLowerCase() !== 'shipping cost',
        )
        .map((lineItem) => {
            const matchingOrderItem = order?.orderItems?.find(
                (item) => item.Id === lineItem.OrderItemSummaryId,
            )

            return {
                name:
                    lineItem.OrderItemSummary?.VariantIdentifier__c ||
                    lineItem.OrderItemSummary?.Name,
                sku: lineItem.OrderItemSummary?.StockKeepingUnit,
                quantity: lineItem.QuantityExpected,
                price: lineItem.TotalAmount,
                currency: matchingOrderItem?.CurrencyIsoCode,
            }
        })

    await generateReturnPDF(
        {
            orderDate: formatDate(new Date(order?.orderedDate), {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }),
            orderNumber: order?.orderNumber,
            returnDate: formatDate(new Date(returnOrder?.CreatedDate), {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }),
            returnNumber: returnOrder?.ReturnOrderNumber,
            barcodeImage: returnOrder?.barcodeImage, // Use barcodeImage (base64 PNG)
            trackingNumber: returnOrder?.TrackingNumber__c || 'N/A',
            customerName: `${order?.firstName} ${order?.lastName}`,
            customerEmail: order?.billingInfo?.email,
            customerAddress: order?.deliveryInfo?.DeliverToStreet,
            products,
        },
        formatMessage,
        order, // Pass order for max return date calculation
    )
}

/**
 * Determines if a subscription can be edited or canceled based on the next renewal date.
 * Allows editing/canceling if the next renewal date is more than 2 days away.
 * @param {object} subscription - The subscription object (must have renewal.nextDate).
 * @returns {boolean} True if editable/cancellable, false otherwise.
 */
export const canEditOrCancelSubscription = (subscription) => {
    if (!subscription?.renewal?.nextDate) return true
    const nextDate = new Date(subscription.renewal.nextDate)
    const now = new Date()
    const diffMs = nextDate - now
    const diffDays = diffMs / (1000 * 60 * 60 * 24)
    return diffDays > 2
}
